import type { Cart } from '@iheartjane/data-access';
import type { GeocodingResult } from '@iheartjane/geolocation';
import type { AppMode } from '@iheartjane/types';
import { createContext, useContext } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';

import type { CheckoutNewRequest } from '@jane/shared-ecomm/types';
// eslint-disable-next-line no-restricted-imports
import type { CheckoutAgreementVersion } from '@jane/shared/models';
import type {
  Address,
  CartLineItems,
  Identification,
  Store,
  Tags,
} from '@jane/shared/types';

import type {
  Customer,
  PreAuthOptions,
  PrepaymentProviders,
} from './checkoutProvider.types';

export interface CheckoutContextType {
  appInfo: {
    appMode: AppMode;
    bloomUserExperience?: boolean;
    checkoutError: string | null;
    disableAuthFeatures?: boolean;
    disablePrepayment?: boolean;
    externalAuth?: boolean;
    headlessCheckoutPartnerId?: string;
    headlessLoginUrl?: string;
    isChangingReservationModes?: boolean;
    isCheckingOut?: boolean;
    isEmbeddedMode: boolean;
    janeDeviceId?: string;
    partnerHostedConfig: {
      isPartnerHosted: boolean;
      partnerHostedPath?: string;
    };
  };
  bagPickupMode: 'pickup' | 'curbside';
  cart: Cart;
  cartLineItems: CartLineItems;
  checkoutRequirements: {
    data: {
      checkout_agreement_versions: CheckoutAgreementVersion[];
      medical_documentation_requirement:
        'photo' | 'number' | 'photo_and_number' | 'none' | null;
      medical_expiration_date_required?: boolean;
    };
    loading: boolean;
  };
  customer: Customer;
  deliveryAddress: Address;
  deliveryValidation: {
    data: {
      can_deliver: boolean | null;
      delivers_today: boolean | null;
      fee: number | null;
      last_call_minutes: number | null;
      lead_time_minutes: number | null;
      message: string | null;
      minimum: number | null;
    };
    loading: boolean;
  };
  dispatches: {
    changeReservationMode: (mode: 'delivery' | 'pickup') => void;
    checkout: (payload: CheckoutNewRequest) => void;
    checkoutCancelled: () => void;
    checkoutError: (error: string) => void;
    checkoutStarted: () => void;
    dismissCheckoutError: () => void;
    openCart: () => void;
    refreshCart: () => void;
    setBagPickupMode: (mode: 'pickup' | 'curbside') => void;
    updateCustomerPickupAddress: (values: {
      errorMessage?: string | null;
      googleValidatedAddress?: GeocodingResult | null;
      selectedPickupAddress?: string | null;
    }) => void;
    updateIdentification: ({
      document,
      file,
    }: {
      document: keyof Identification;
      file: string | null;
    }) => void;
    updatePaymentMethod: (paymentMethod: string | null) => void;
    updateTip: (value: number) => void;
    updateUser: (values: {
      birthDate?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      promotionsOptOut?: boolean;
    }) => void;
  };
  googleValidatedAddress: GeocodingResult | null;
  guestCheckout: {
    preAuthSelection: PrepaymentProviders | 'janeGold' | null;
    setPreAuthCustomer: Dispatch<SetStateAction<Partial<Customer>>>;
    setPreAuthOptions: Dispatch<SetStateAction<PreAuthOptions>>;
    setPreAuthSelection: Dispatch<
      SetStateAction<PrepaymentProviders | 'janeGold' | null>
    >;
    setShowLoginModal: Dispatch<SetStateAction<boolean>>;
    showLoginModal: boolean;
  };
  identification: {
    document: Identification;
    isUploading: boolean;
  };
  pickupAddressErrorMessage: string | null | undefined;
  prepaymentProviders: PrepaymentProviders[];
  selectedPickupAddress: string | null;
  store: Store;
  tags: Tags;
  userGroupSpecialId?: string | number;
}

const CheckoutContext = createContext<CheckoutContextType>(
  {} as CheckoutContextType
);

interface Props {
  children: ReactNode;
  value: CheckoutContextType;
}

export const CheckoutProvider = ({ children, value }: Props) => {
  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => {
  const context = useContext(CheckoutContext);

  if (!Object.keys(context).length) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }

  return context;
};
